import { useMemo, useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { API_URL, fetchEndpointData } from 'api';
import { filterTruthy } from 'utils/collection';
import { empty } from 'utils/helpers';
import { getLastPathSegment, joinPath } from 'utils/url';

import { useSearchTermGTM } from './gtm';
import { useMaxWidth } from './useMediaQuery';
import { usePrevious } from './usePrevious';
import { useValueChangeEffect } from './useValueChangeEffect';

export function useSearchProductsPageSize() {
	const isSmallOrSmaller = useMaxWidth('md');
	return isSmallOrSmaller ? 4 : 5;
}
export function useSearchTermsPageSize() {
	const isSmallOrSmaller = useMaxWidth('md');
	return isSmallOrSmaller ? 4 : 7;
}

/** Hook for the primary product search. */
export function useSearch(query: string) {
	const productsPageSize = useSearchProductsPageSize();
	const termsPageSize = useSearchTermsPageSize();
	const { sendSearchTermBrandViewEvent, sendSearchTermCategoryViewEvent } =
		useSearchTermGTM();
	const {
		data: currentData,
		error,
		isLoading,
	} = useSWRImmutable(
		query ? joinPath(API_URL, 'Search/advanced/', query) : null,
		async (url) => {
			const result = await fetchEndpointData({
				name: 'PostSearchAdvanced',
				body: {
					directSearch: true,
					searchTerm: getLastPathSegment(url),
					products: { offset: 0, pageSize: productsPageSize },
					brands: { offset: 0, pageSize: 4 },
					categories: { offset: 0, pageSize: 3 },
					stores: { offset: 0, pageSize: 3 },
					inspirationPages: { offset: 0, pageSize: 3 },
				},
			});
			if (result?.categorySearchResponse?.categories?.length) {
				sendSearchTermCategoryViewEvent();
			}
			if (result?.brandSearchResponse?.brands?.length) {
				sendSearchTermBrandViewEvent();
			}
			return result;
		},
	);

	// Keep the previous result until a new one is loaded, but only use it for
	// 'refinement' searches, i.e. going from one result to the next. Emptying
	// the query makes the next search behave like a new one. Otherwise it will
	// feel a bit janky - emptying the search field flips the result back to
	// popular items and entering a new search would then change that to
	// `prevData` items until the new result is loaded.
	// Could use the `keepPreviousData` SWR option but that requires clearing
	// the cache in some cases (like when the search field is emptied) and
	// keeping track of the correct key to clear (it will never be the current
	// one) has its own challenges.
	const prevData = usePrevious(currentData, { ignoreUndefined: true });
	const [hasPrevQuery, setHasPrevQuery] = useState(false);
	useValueChangeEffect(isLoading, () => {
		// Finished loading and still have a query.
		if (!isLoading && query) {
			setHasPrevQuery(true);
		}
	});
	useValueChangeEffect(query, () => {
		// Emptied the search field.
		if (!query) {
			setHasPrevQuery(false);
		}
	});
	const data = currentData ?? (hasPrevQuery ? prevData : undefined);

	const categories = useMemo(
		() => filterTruthy(data?.categorySearchResponse?.categories, 'url'),
		[data?.categorySearchResponse?.categories],
	);
	const brands = useMemo(
		() => filterTruthy(data?.brandSearchResponse?.brands, 'url'),
		[data?.brandSearchResponse?.brands],
	);
	const products = useMemo(
		() => filterTruthy(data?.productSearchResponse?.products, 'url'),
		[data?.productSearchResponse?.products],
	);
	const stores = useMemo(
		() => filterTruthy(data?.storeSearchResponse?.stores, 'url'),
		[data?.storeSearchResponse?.stores],
	);
	const inspirationPages = useMemo(
		() =>
			filterTruthy(
				data?.inspirationPageSearchResponse?.inspirationPages.map((page) => ({
					...page,
					text: page.fields?.navigationTitle?.value,
				})),
				'url',
				'text',
			),
		[data?.inspirationPageSearchResponse?.inspirationPages],
	);

	return {
		searchTerms:
			data?.autocompleteQueries?.slice(0, termsPageSize) ?? empty.array,
		categories,
		brands,
		products,
		stores,
		inspirationPages,
		totalProductCount: data?.productSearchResponse?.total ?? 0,
		spellingSuggestions:
			data?.productSearchResponse?.spellingSuggestions ?? empty.array,
		isLoading,
		hasResultResponse: Boolean(data),
		error,
	};
}
